import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  Card,
  CardActionArea,
  Grid,
  Skeleton,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add, Lightbulb, KeyboardArrowDown, Storefront } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import {
  FEATURE_REQUEST_CATEGORIES,
  FeatureRequestListItem,
  FeatureRequestListResponse,
} from '../types/featureRequests';
import StatusPill from '../components/featureRequests/StatusPill';
import CategoryChip from '../components/featureRequests/CategoryChip';
import VoteButton from '../components/featureRequests/VoteButton';
import SegmentedTabs from '../components/featureRequests/SegmentedTabs';
import SubmitFeatureRequestDialog from '../components/featureRequests/SubmitFeatureRequestDialog';
import { colors } from '../theme/theme';

type TabKey = 'top' | 'new' | 'planned' | 'in_progress' | 'shipped';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'top', label: 'Top' },
  { key: 'new', label: 'New' },
  { key: 'planned', label: 'Planned' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'shipped', label: 'Shipped' },
];

// Small self-contained "time ago" formatter — the project's date-fns install
// ships v4, but a stale @types/date-fns v2 stub still shadows its types
// (declares the old `distanceInWordsToNow` API, not `formatDistanceToNow*`),
// so a named import for the real function fails type-checking. Not worth
// fighting that here.
const timeAgo = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

// Rank 1-3 get a small circular badge; everyone else gets a plain "#n" label.
const RANK_BADGE_STYLE = [
  { bg: colors.primary, text: '#ffffff' },
  { bg: '#ffe1ec', text: colors.secondary },
  { bg: '#fdf1f5', text: colors.secondary },
];

const FeatureRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('top');
  const [category, setCategory] = useState('');
  const [items, setItems] = useState<FeatureRequestListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error'; requestId?: string } | null>(null);

  const fetchList = useCallback(async (targetPage: number, append: boolean) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page: targetPage };
      if (tab === 'top' || tab === 'new') {
        params.sort = tab;
      } else {
        params.status = tab;
      }
      if (category) params.category = category;

      const response = await apiService.get<FeatureRequestListResponse>(
        API_ENDPOINTS.FEATURE_REQUESTS.LIST,
        { params },
      );
      setItems((prev) => (append ? [...prev, ...response.items] : response.items));
      setTotal(response.total);
      setPage(targetPage);
    } catch (err: any) {
      setError(err?.message || 'Could not load feature requests.');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [tab, category]);

  useEffect(() => {
    fetchList(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, category]);

  const handleVoteToggle = async (item: FeatureRequestListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const nextHasVoted = !item.hasVoted;
    const delta = nextHasVoted ? 1 : -1;
    setItems((prev) =>
      prev.map((i) =>
        i._id === item._id ? { ...i, hasVoted: nextHasVoted, voteCount: i.voteCount + delta } : i,
      ),
    );
    try {
      if (nextHasVoted) {
        // Pinned to the account's own studio under "All locations".
        await apiService.post(API_ENDPOINTS.FEATURE_REQUESTS.VOTE(item._id), undefined, apiService.atOwnStudio());
      } else {
        await apiService.delete(API_ENDPOINTS.FEATURE_REQUESTS.VOTE(item._id), apiService.atOwnStudio());
      }
    } catch (err: any) {
      // Revert the optimistic update.
      setItems((prev) =>
        prev.map((i) =>
          i._id === item._id ? { ...i, hasVoted: item.hasVoted, voteCount: item.voteCount } : i,
        ),
      );
      setSnackbar({ message: err?.message || 'Could not update your vote.', severity: 'error' });
    }
  };

  const handleCreated = (created: FeatureRequestListItem) => {
    setSnackbar({ message: 'Request submitted!', severity: 'success', requestId: created._id });
    if (tab === 'new' || tab === 'top') {
      fetchList(1, false);
    }
  };

  const handleVotedExisting = () => {
    fetchList(1, false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#1a1a20', letterSpacing: '-0.5px' }}>
            Feature Requests
          </Typography>
          <Typography sx={{ fontSize: 14, color: colors.greyText, mt: 0.5 }}>
            Vote for what SweatSocial should build next.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setSubmitOpen(true)}
          sx={{
            borderRadius: '12px',
            padding: '11px 20px',
            boxShadow: `0 4px 14px ${colors.primary}66`,
            '&:hover': { boxShadow: `0 8px 22px ${colors.primary}80` },
          }}
        >
          Submit a request
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <SegmentedTabs value={tab} options={TABS} onChange={setTab} />

        <TextField
          select
          size="small"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          SelectProps={{ IconComponent: KeyboardArrowDown }}
          sx={{
            minWidth: 190,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: colors.background,
            },
          }}
        >
          <MenuItem value="">All categories</MenuItem>
          {FEATURE_REQUEST_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4].map((n) => (
            <Grid key={n} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={128} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && !error && items.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            borderRadius: '16px',
            border: `1px dashed ${colors.divider}`,
            backgroundColor: colors.backgroundTertiary,
          }}
        >
          <Lightbulb sx={{ fontSize: 48, color: colors.primary, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {category || tab !== 'top' ? 'Nothing here yet' : 'No feature requests yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
            Got an idea that would make SweatSocial work better for your studio? Be the first to
            submit one, and other studios can vote it up.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setSubmitOpen(true)}>
            Submit the first request
          </Button>
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <Grid container spacing={2.5}>
            {items.map((item, index) => {
              const rankStyle = tab === 'top' ? RANK_BADGE_STYLE[index] : undefined;
              return (
                <Grid key={item._id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      border: `1px solid ${colors.divider}`,
                      boxShadow: `0 2px 12px ${colors.primary}14`,
                      transition: 'box-shadow 180ms ease, transform 180ms ease',
                      '&:hover': {
                        boxShadow: `0 12px 28px ${colors.primary}38`,
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/feature-requests/${item._id}`)}
                      sx={{
                        height: '100%',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0, pt: tab === 'top' ? 0 : 0.5 }}>
                          {tab === 'top' ? (
                            rankStyle ? (
                              <Box
                                sx={{
                                  width: 26, height: 26, borderRadius: '50%',
                                  backgroundColor: rankStyle.bg, color: rankStyle.text,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 12, fontWeight: 800,
                                }}
                              >
                                {index + 1}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.greyText }}>#{index + 1}</Typography>
                            )
                          ) : null}
                          <VoteButton voteCount={item.voteCount} hasVoted={item.hasVoted} onToggle={(e) => handleVoteToggle(item, e)} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16.5,
                              fontWeight: 800,
                              color: '#1a1a20',
                              lineHeight: 1.4,
                              letterSpacing: '-0.2px',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                            <StatusPill status={item.status} size="medium" />
                            <CategoryChip category={item.category} size="medium" />
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mt: 2,
                          pt: 1.5,
                          borderTop: `1px solid ${colors.divider}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Storefront sx={{ fontSize: 15, color: colors.textSecondary, flexShrink: 0 }} />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: colors.textPrimary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.submittedByStudio?.studioName || 'A studio'}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, flexShrink: 0 }}>
                          {timeAgo(item.createdAt)}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {items.length < total && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button variant="outlined" onClick={() => fetchList(page + 1, true)} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}

      <SubmitFeatureRequestDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onCreated={handleCreated}
        onVotedExisting={handleVotedExisting}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            action={
              snackbar.requestId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    navigate(`/feature-requests/${snackbar.requestId}`);
                    setSnackbar(null);
                  }}
                >
                  View
                </Button>
              ) : undefined
            }
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FeatureRequestsPage;
